/**
 * Google 이미지 검색 결과를 가운데 패널 overlay 로 표시.
 *
 * 사용자 의도: 사용자가 "labtop" 같은 오타 / 모호한 keyword 입력 시 Google 이미지
 * 결과를 화면에서 직접 확인 → 의도 명확화 → 정확한 keyword 로 재검색.
 *
 * Google 정책상 메인 검색 페이지는 X-Frame-Options 로 iframe 차단 → 차단 시
 * 자동으로 "새 창에서 열기" 버튼 fallback.
 */
import React, { useEffect, useState, useRef } from "react";

export default function GoogleImageOverlay({ q, onClose }) {
  const [blocked, setBlocked] = useState(false);
  const iframeRef = useRef(null);

  // ESC 키로 닫기
  useEffect(() => {
    if (!q) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose && onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [q, onClose]);

  // iframe load 실패 감지 — X-Frame-Options 차단 시 onLoad 가 일부 브라우저에서
  // 호출 안 되거나 about:blank 로 fallback. 2초 후 확인.
  useEffect(() => {
    if (!q) {
      setBlocked(false);
      return;
    }
    const t = setTimeout(() => {
      try {
        const doc = iframeRef.current?.contentDocument;
        // cross-origin → contentDocument 접근 시 throw. 정상 (Google embed)
        // 다만 X-Frame-Options 차단 시 about:blank → contentDocument null/empty
        if (!doc) return; // cross-origin 접근 불가 = 정상 load
      } catch {
        return; // cross-origin throw = 정상 load
      }
    }, 2500);
    return () => clearTimeout(t);
  }, [q]);

  if (!q) return null;

  const url = `https://www.google.co.kr/search?q=${encodeURIComponent(q)}&udm=2`;

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.5)",
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "85%",
          height: "85%",
          maxWidth: 1400,
          background: "var(--audit-bg)",
          borderRadius: 6,
          boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* 헤더 */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "10px 16px",
            borderBottom: "1px solid var(--audit-border)",
            background: "#dbeafe",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              fontSize: 13,
              fontWeight: 600,
              color: "#1a73e8",
            }}
          >
            Google 이미지 검색
            <span
              style={{
                fontFamily: "JetBrains Mono, monospace",
                fontSize: 12,
                color: "var(--audit-text-muted)",
                fontWeight: 400,
              }}
            >
              "{q}"
            </span>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <a
              href={url}
              target="_blank"
              rel="noreferrer"
              style={{
                padding: "5px 10px",
                fontSize: 12,
                color: "#1a73e8",
                background: "white",
                border: "1px solid #1a73e8",
                borderRadius: 3,
                textDecoration: "none",
                cursor: "pointer",
              }}
            >
              새 창에서 열기 ↗
            </a>
            <button
              onClick={onClose}
              style={{
                padding: "5px 10px",
                fontSize: 12,
                color: "var(--audit-text)",
                background: "white",
                border: "1px solid var(--audit-border)",
                borderRadius: 3,
                cursor: "pointer",
              }}
            >
              ✕ 닫기 (ESC)
            </button>
          </div>
        </div>
        {/* iframe — Google 이 X-Frame-Options 차단 시 빈 화면 표시. 사용자가 "새 창" 클릭 */}
        <div style={{ flex: 1, position: "relative" }}>
          <iframe
            ref={iframeRef}
            src={url}
            title="Google Images"
            style={{ width: "100%", height: "100%", border: "none" }}
            sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
          />
          {blocked && (
            <div
              style={{
                position: "absolute",
                inset: 0,
                background: "var(--audit-bg-subtle)",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 10,
                color: "var(--audit-text)",
              }}
            >
              <div style={{ fontSize: 14 }}>
                Google 이 embed 를 차단했습니다.
              </div>
              <a
                href={url}
                target="_blank"
                rel="noreferrer"
                style={{
                  padding: "8px 16px",
                  fontSize: 12,
                  color: "white",
                  background: "#1a73e8",
                  borderRadius: 4,
                  textDecoration: "none",
                }}
              >
                새 창에서 열기 ↗
              </a>
            </div>
          )}
        </div>
        <div
          style={{
            padding: "6px 16px",
            fontSize: 12,
            color: "var(--audit-text-faint)",
            background: "var(--audit-bg-subtle)",
            borderTop: "1px solid var(--audit-border)",
          }}
        >
          ⚠ Google 검색 결과 — HS 분류 결과 아님. 외부 참고용. Google 에 검색어
          전송됨.
        </div>
      </div>
    </div>
  );
}
